// ============================================
// HEATMAP VIEW - Team overview grid
// ============================================

import React, { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import type { Employee } from '../../types/results';
import { getHebrewName } from '../../constants/muscles';
import { getScoreColor } from '../../utils/colors';
import type { DemographicDimension } from '../../constants/demographics';
import { groupEmployeesByDemographic, getSegmentLabel, getSegmentColor } from '../../services/demographics';

interface HeatmapViewProps {
    employees: Employee[];
    onSelectEmployee: (id: string) => void;
    demographicDimension?: DemographicDimension | null;
}

export default function HeatmapView({ employees, onSelectEmployee, demographicDimension }: HeatmapViewProps) {
    if (employees.length === 0) {
        return (
            <div className="analytics-empty-state">
                <p>אין נתונים להצגה</p>
            </div>
        );
    }

    // Get all categories from the first employee
    const categories = Object.keys(employees[0].scores);

    const sortedEmployees = useMemo(() => {
        if (!demographicDimension) return [{ segment: null, employees }];

        const groups = groupEmployeesByDemographic(employees, demographicDimension);
        const result: { segment: string | null; employees: Employee[] }[] = [];

        groups.forEach((emps, segKey) => {
            result.push({ segment: segKey, employees: emps });
        });

        // Add employees with 'unknown' segment at the end
        const groupedIds = new Set<string>();
        groups.forEach(emps => emps.forEach(e => groupedIds.add(e.id)));
        const ungrouped = employees.filter(e => !groupedIds.has(e.id));
        if (ungrouped.length > 0) {
            result.push({ segment: null, employees: ungrouped });
        }

        return result;
    }, [employees, demographicDimension]);

    return (
        <div className="analytics-heatmap-container">
            <div
                className="analytics-heatmap-grid"
                style={{
                    gridTemplateColumns: `120px repeat(${categories.length}, 1fr)`,
                }}
            >
                {/* Header Row */}
                <div className="analytics-heatmap-header analytics-heatmap-sticky">עובד</div>
                {categories.map(cat => (
                    <div
                        key={cat}
                        className="analytics-heatmap-header"
                        title={getHebrewName(cat)}
                    >
                        {getHebrewName(cat)}
                    </div>
                ))}

                {/* Data Rows grouped by segment */}
                {sortedEmployees.map((group, gIdx) => (
                    <React.Fragment key={`group-${gIdx}`}>
                        {/* Segment header row */}
                        {demographicDimension && group.segment && (
                            <div
                                className="analytics-heatmap-segment-header"
                                style={{
                                    gridColumn: `1 / -1`,
                                    padding: '6px 12px',
                                    backgroundColor: `${getSegmentColor(group.segment)}20`,
                                    borderRight: `3px solid ${getSegmentColor(group.segment)}`,
                                    fontWeight: 700,
                                    fontSize: '12px',
                                    color: 'var(--neutral-700)',
                                    direction: 'rtl',
                                }}
                            >
                                {getSegmentLabel(group.segment, demographicDimension)} ({group.employees.length})
                            </div>
                        )}

                        {group.employees.map(emp => (
                            <React.Fragment key={`emp-${emp.id}`}>
                                <div
                                    className="analytics-heatmap-name analytics-heatmap-sticky"
                                    onClick={() => onSelectEmployee(emp.id)}
                                >
                                    {emp.name}
                                    <ArrowLeft size={12} className="analytics-heatmap-arrow" />
                                </div>
                                {categories.map(cat => {
                                    const val = emp.scores[cat] ?? 0;
                                    return (
                                        <div
                                            key={`${emp.id}-${cat}`}
                                            className="analytics-heatmap-cell"
                                            style={{ backgroundColor: getScoreColor(val) }}
                                        >
                                            {val.toFixed(2)}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}
